import { readCalendarChangeReview, type CalendarChangeReview } from "@/src/features/calendar/calendarChangeReview";
import type { ApprovalHandoffContract } from "@/src/features/jobs/continuity/contracts";
import type { Job } from "@/src/features/jobs/jobManager";
import { isStandingMandateKind, type StandingMandateKind } from "@/src/features/mandates/standingMandate";
import {
  describeHeldAction,
  describeReviewDetails,
  type ReviewDetail,
} from "@/src/features/jobs/workflowStatusModel";

/**
 * A held action, decided from Notifications. The workflow monitor only shows
 * that a request waits on the person; this row is where they read what would
 * happen and say yes or no. Email never lands here: the assistant only drafts
 * it, into the email component, and the person sends it from there. The row is written when the job pauses and
 * removed once the approval route records a decision.
 */
export const ACTION_APPROVAL_NOTIFICATION_TYPE = "action_approval";

const ID_PREFIX = "action-approval:";

export type ActionApprovalDecision = "approve" | "reject";

/**
 * The yes-or-no question a held action asks, written by the code that held
 * it (the hosted address proposal, mailboxAddress.ts), never by a model.
 * `askedInTurn` names the chat turn whose answer already asked it, so a bare
 * "yes" right after that turn answers this card, and the row is not read out
 * a second time.
 */
export type ActionApprovalQuestion = {
  text: string;
  /** The same question for the ear: an address spelled out letter by letter. */
  spoken: string;
  askedInTurn?: string;
};

/** What the row shows and posts. Read from the job when it pauses. */
export type ActionApprovalCard = {
  jobId: string;
  approvalId: string;
  idempotencyKey: string;
  title: string;
  summary: string;
  /** What happens, one labelled line per field. A message to send comes first, whole. */
  details: ReviewDetail[];
  sendsMessage: boolean;
  /** False for a send whose message cannot be read yet: it may only be declined. */
  canApprove: boolean;
  /** A calendar change, drawn as the event moving rather than as fields. */
  calendar?: CalendarChangeReview;
  /** The question the hold asks, when its code wrote one. */
  question?: ActionApprovalQuestion;
  /**
   * The standing mandate this card can turn on ("Always do this"). Only a
   * hint for the row: the grant itself is written by the mandate route in
   * the user's own session, never by whoever wrote this card.
   */
  mandateOffer?: StandingMandateKind;
  /**
   * The calendar items a bound calendar change reaches, at the revisions the
   * card was issued for (see {@link boundCalendarItemsOfJob}). Stored with
   * the row so a feed read can tell the card went stale (an item deleted or
   * edited since) without opening the job's signed change. Server-side only:
   * {@link readActionApprovalCard} does not carry it to the client.
   */
  boundCalendarItems?: ActionApprovalBoundCalendarItem[];
};

/** One calendar item a card's signed change is fenced on (`BoundCalendarItem`, calendarBoundChange.ts). */
export type ActionApprovalBoundCalendarItem = {
  id: string;
  /** The item's revision when the card was issued; null for an item that had none. */
  updatedDate: number | null;
  /** For an item reached through a moved occurrence: the series revision the write is fenced on. */
  seriesUpdatedDate?: number;
};

/** At most this many bound items are stamped on a row; a rain move binds one. */
export const ACTION_APPROVAL_BOUND_ITEMS_LIMIT = 25;
const BOUND_ITEM_ID_LIMIT = 512;

const QUESTION_LIMIT = 600;
const TURN_ID_LIMIT = 200;

/** A well-formed question, or null. `previewId` must match when given. */
function readApprovalQuestion(value: unknown, previewId?: string): ActionApprovalQuestion | null {
  const question = record(value);
  if (!question) return null;
  if (previewId !== undefined && question.previewId !== previewId) return null;
  const questionText = typeof question.text === "string" ? question.text.trim() : "";
  const spoken = typeof question.spoken === "string" ? question.spoken.trim() : "";
  if (!questionText || !spoken || questionText.length > QUESTION_LIMIT || spoken.length > QUESTION_LIMIT) return null;
  const askedInTurn = typeof question.askedInTurn === "string" && question.askedInTurn && question.askedInTurn.length <= TURN_ID_LIMIT
    ? question.askedInTurn
    : undefined;
  return { text: questionText, spoken, ...(askedInTurn ? { askedInTurn } : {}) };
}

/** One row per approval, so a chain's next hold gets its own row. */
export function actionApprovalNotificationId(approvalId: string): string {
  return `${ID_PREFIX}${approvalId}`;
}

/** The approval route, as `makePostRequest` takes it (relative to `/api/`). */
export function actionApprovalDecisionPath(jobId: string): string {
  return `action-approvals?${new URLSearchParams({ jobId }).toString()}`;
}

/**
 * An approval names the chat turn it was given in (`reactId`), so the route
 * can write the job's outcome into that turn. A decline runs nothing, so it
 * names none.
 */
export function actionApprovalDecisionBody(
  card: ActionApprovalCard,
  decision: ActionApprovalDecision,
  turn?: { reactId: string },
) {
  return {
    approvalId: card.approvalId,
    decision,
    idempotencyKey: card.idempotencyKey,
    ...(decision === "approve" && turn?.reactId ? { reactId: turn.reactId } : {}),
  };
}

/** The two verbs, in the words the row uses. */
export function actionApprovalVerbs(card: Pick<ActionApprovalCard, "sendsMessage">) {
  return card.sendsMessage
    ? { approve: "Send", reject: "Don't send" }
    : { approve: "Approve", reject: "Decline" };
}

/**
 * The card for a job's pending approval, or null when nothing can be decided:
 * the approval is not pending, or carries no decision requests.
 */
export function buildActionApprovalCard(job: Job, approval: ApprovalHandoffContract | undefined): ActionApprovalCard | null {
  if (!approval || approval.status !== "pending" || !approval.canDecide) return null;
  const idempotencyKey = approval.decisions?.approve.body.idempotencyKey;
  if (!idempotencyKey || approval.decisions?.reject.body.idempotencyKey !== idempotencyKey) return null;
  const held = describeHeldAction(job, approval);
  const metadata = record(record(job.data)?.metadata);
  const calendar = readCalendarChangeReview(metadata?.calendarChangeReview);
  // Only the question written for this very approval asks about it.
  const question = held.preview ? readApprovalQuestion(metadata?.approvalQuestion, held.preview.previewId) : null;
  const offer = record(metadata?.standingMandateOffer);
  const offerKind = offer?.kind;
  const mandateOffer = held.preview && isStandingMandateKind(offerKind) && offer?.previewId === held.preview.previewId && held.canApprove
    ? offerKind
    : undefined;
  const boundCalendarItems = held.preview ? boundCalendarItemsOfJob(job, held.preview.previewId) : [];
  return {
    jobId: job.jobId,
    approvalId: approval.approvalId,
    idempotencyKey,
    title: approval.title,
    // Without a planner reason the preview names the internal function; say nothing then.
    summary: question?.text
      ?? (held.preview && approval.summary === `Run ${held.preview.functionName}.` ? "" : approval.summary),
    // The message shown is exactly the one whose presence makes the send approvable.
    details: held.draft
      ? [{ label: "Message", value: held.draft.body }, ...describeReviewDetails(held.preview, { withoutMessage: true })]
      : describeReviewDetails(held.preview),
    sendsMessage: held.sendsMessage,
    canApprove: held.canApprove,
    // Only the review written for this very approval describes it.
    ...(calendar && held.preview && calendar.previewId === held.preview.previewId ? { calendar } : {}),
    ...(question ? { question } : {}),
    ...(mandateOffer ? { mandateOffer } : {}),
    ...(boundCalendarItems.length > 0 ? { boundCalendarItems } : {}),
  };
}

/** The `argumentValue` of the action argument named `name` (`args` is `[{ argumentName, argumentValue }]`). */
function actionArgumentOf(args: unknown, name: string): unknown {
  if (!Array.isArray(args)) return undefined;
  return args.map(record).find((arg) => arg?.argumentName === name)?.argumentValue;
}

/** The JSON body of a signed bound change (`<base64url body>.<mac>`), or null. */
function boundChangeBodyOf(token: unknown): Record<string, unknown> | null {
  if (typeof token !== "string") return null;
  const separator = token.lastIndexOf(".");
  if (separator <= 0) return null;
  try {
    const base64 = token.slice(0, separator).replace(/-/g, "+").replace(/_/g, "/");
    const bytes = Uint8Array.from(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")), (char) => char.charCodeAt(0));
    return record(JSON.parse(new TextDecoder().decode(bytes)));
  } catch {
    return null;
  }
}

const revisionOf = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * The calendar items the held action's bound change reaches (its update and
 * delete operations), at the revisions it was issued for. The action is the
 * one the approval's preview names: the job itself, or a step of its chain.
 *
 * The change is read, not verified: verifying needs the server's signing
 * secret, and this module also runs on the client. The items only ever let a
 * feed read hide a card that went stale, never run or approve anything, and
 * the replay verifies the signed change before it writes.
 */
export function boundCalendarItemsOfJob(job: Pick<Job, "data">, previewId: string): ActionApprovalBoundCalendarItem[] {
  const data = record(job.data);
  if (!data) return [];
  const actions = [data, ...(Array.isArray(data.chain) ? data.chain.map(record) : [])];
  const held = actions.find((action) => record(action?.actionTrust)?.previewId === previewId);
  const operations = boundChangeBodyOf(actionArgumentOf(held?.args, "boundCalendarChange"))?.operations;
  if (!Array.isArray(operations)) return [];
  const items = new Map<string, ActionApprovalBoundCalendarItem>();
  for (const operation of operations.map(record)) {
    if ((operation?.op !== "update_item" && operation?.op !== "delete_item") || !Array.isArray(operation.items)) continue;
    for (const item of operation.items.map(record)) {
      const id = typeof item?.id === "string" ? item.id : "";
      if (!id || id.length > BOUND_ITEM_ID_LIMIT || items.has(id)) continue;
      const series = revisionOf(item?.seriesUpdatedDate);
      items.set(id, { id, updatedDate: revisionOf(item?.updatedDate), ...(series !== null ? { seriesUpdatedDate: series } : {}) });
    }
  }
  return [...items.values()].slice(0, ACTION_APPROVAL_BOUND_ITEMS_LIMIT);
}

/**
 * The bound items a stored card was stamped with, as
 * {@link boundCalendarItemsOfJob} stamped them; empty for a card issued
 * before the stamp. An entry without a well-formed id or revision is skipped.
 */
export function readActionApprovalBoundItems(card: unknown): ActionApprovalBoundCalendarItem[] {
  const value = record(card)?.boundCalendarItems;
  if (!Array.isArray(value)) return [];
  const items: ActionApprovalBoundCalendarItem[] = [];
  for (const entry of value.slice(0, ACTION_APPROVAL_BOUND_ITEMS_LIMIT)) {
    const item = record(entry);
    const id = typeof item?.id === "string" ? item.id : "";
    if (!item || !id || id.length > BOUND_ITEM_ID_LIMIT || !("updatedDate" in item)) continue;
    const updatedDate = revisionOf(item.updatedDate);
    if (item.updatedDate !== null && updatedDate === null) continue;
    const series = revisionOf(item.seriesUpdatedDate);
    items.push({ id, updatedDate, ...(series !== null ? { seriesUpdatedDate: series } : {}) });
  }
  return items;
}

/** A stored card's fence on one calendar item: the revision it was issued for, and when it was issued. */
export type StoredCardCalendarBinding = { itemId: string; updatedDate: number | null; issuedAt: number };

/**
 * The calendar items stored approval cards are fenced on, read from feed rows
 * as the data layer returns them (extras lifted, or still nested). A producer
 * whose own write would void an open card reads these to tell whether that
 * card still stands: a card fenced on another revision than the item's
 * current one is already void (the feed hides it and the replay refuses it).
 */
export function storedCardCalendarBindings(rows: readonly unknown[]): StoredCardCalendarBinding[] {
  const bindings: StoredCardCalendarBinding[] = [];
  for (const value of rows) {
    const row = record(value);
    if (row?.notificationType !== ACTION_APPROVAL_NOTIFICATION_TYPE) continue;
    const issuedAt = revisionOf(row.createdDate) ?? revisionOf(row.storedDate);
    if (issuedAt === null) continue;
    const card = record(row.actionApproval) ?? record(record(row.extraFields)?.actionApproval);
    for (const item of readActionApprovalBoundItems(card)) {
      bindings.push({ itemId: item.id, updatedDate: item.updatedDate, issuedAt });
    }
  }
  return bindings;
}

/**
 * The stored row. `actionRequired` holds it at the top of the feed until it
 * is decided, and it lapses with the approval itself.
 */
export function actionApprovalNotificationDocument(input: {
  userId: string;
  card: ActionApprovalCard;
  now: number;
  expiresAt: number;
}) {
  const id = actionApprovalNotificationId(input.card.approvalId);
  return {
    id,
    userId: input.userId,
    source: "smartmanager",
    notificationType: ACTION_APPROVAL_NOTIFICATION_TYPE,
    presentationMode: "digest",
    // A change offered unasked leads with why; that sentence is what the feed
    // and the chat read out. It is stored as written, never rewritten. A
    // code-written question is read out the same way, and spoken in its own
    // words (an address spelled out).
    summary: input.card.calendar?.lead || input.card.question?.text || input.card.title,
    ...(input.card.question ? { summaryAudio: input.card.question.spoken } : {}),
    urgency: 3,
    actionRequired: true,
    sourceObjectType: "job",
    sourceObjectId: input.card.jobId,
    dedupeKey: id,
    storedDate: input.now,
    createdDate: input.now,
    updatedDate: input.now,
    expiresAt: input.expiresAt,
    extraFields: { actionApproval: input.card },
  };
}

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
const nonEmpty = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

function readDetails(value: unknown): ReviewDetail[] | null {
  if (!Array.isArray(value)) return null;
  const details = value.map(record);
  return details.every((detail) => detail && nonEmpty(detail.label) && nonEmpty(detail.value))
    ? details.map((detail) => ({ label: String(detail?.label), value: String(detail?.value) }))
    : null;
}

/**
 * The card on a feed row, or null when the row is not a well-formed approval.
 * A feed row carries its stored extras lifted onto the row; the widget's
 * items (`buildNotificationFeedItems`) and client-built rows nest them under
 * `extraFields`. Both are read.
 */
export function readActionApprovalCard(item: {
  notificationType?: unknown;
  extraFields?: unknown;
  actionApproval?: unknown;
}): ActionApprovalCard | null {
  if (item.notificationType !== ACTION_APPROVAL_NOTIFICATION_TYPE) return null;
  const card = record(record(item.extraFields)?.actionApproval ?? item.actionApproval);
  if (!card) return null;
  const details = readDetails(card.details);
  // A malformed review costs the picture, not the decision: the row falls back to the summary.
  const calendar = card.calendar === undefined ? null : readCalendarChangeReview(card.calendar);
  const question = card.question === undefined ? null : readApprovalQuestion(card.question);
  if (
    !nonEmpty(card.jobId)
    || !nonEmpty(card.approvalId)
    || !nonEmpty(card.idempotencyKey)
    || !nonEmpty(card.title)
    || typeof card.summary !== "string"
    || typeof card.sendsMessage !== "boolean"
    || typeof card.canApprove !== "boolean"
    || details === null
  ) return null;
  return {
    jobId: card.jobId,
    approvalId: card.approvalId,
    idempotencyKey: card.idempotencyKey,
    title: card.title,
    summary: card.summary,
    details,
    sendsMessage: card.sendsMessage,
    canApprove: card.canApprove,
    ...(calendar ? { calendar } : {}),
    ...(question ? { question } : {}),
    ...(isStandingMandateKind(card.mandateOffer) && card.canApprove ? { mandateOffer: card.mandateOffer } : {}),
  };
}

export type ActionApprovalPost = (path: string, body: unknown, signal?: AbortSignal) => Promise<unknown>;

/**
 * A decision that did not go through, in words the row can show. `settled`
 * means the approval no longer waits on the person and the route removed its
 * row, so the row should go here too.
 */
export class ActionApprovalDecisionError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly settled = false,
    /** The turn the job reports to, when the route recorded the answer anyway. */
    readonly conversation: ActionApprovalOutcome["conversation"] = null,
  ) {
    super(message);
    this.name = "ActionApprovalDecisionError";
  }
}

const isAbort = (error: unknown) => error instanceof DOMException && error.name === "AbortError";

/** What the route said about a recorded answer. */
export type ActionApprovalOutcome = {
  /** The chat turn the job's outcome is written to, when it has one. */
  conversation: { chatId: string; reactId: string } | null;
};

function readOutcome(response: unknown): ActionApprovalOutcome {
  const approval = record(record(response)?.approval);
  const conversation = record(approval?.conversation);
  const chatId = conversation?.chatId;
  const reactId = conversation?.reactId;
  return {
    conversation: typeof chatId === "string" && chatId && typeof reactId === "string" && reactId
      ? { chatId, reactId }
      : null,
  };
}

/**
 * Send the person's answer to the approval route. Resolves once the route
 * recorded it; throws {@link ActionApprovalDecisionError} otherwise. A send
 * that cannot be read is never approved from here.
 */
export async function decideActionApproval(
  card: ActionApprovalCard,
  decision: ActionApprovalDecision,
  { post, signal, turn }: { post: ActionApprovalPost; signal?: AbortSignal; turn?: { reactId: string } },
): Promise<ActionApprovalOutcome> {
  if (decision === "approve" && !card.canApprove) {
    throw new ActionApprovalDecisionError("not_approvable", "This message is written when it runs, so it can't be sent from here.");
  }
  try {
    return readOutcome(await post(actionApprovalDecisionPath(card.jobId), actionApprovalDecisionBody(card, decision, turn), signal));
  } catch (error) {
    if (isAbort(error)) throw error;
    const failure = error && typeof error === "object" ? (error as { status?: unknown; body?: unknown }) : {};
    const body = record(failure.body);
    const code = String(body?.code ?? "");
    if (failure.status === 401) throw new ActionApprovalDecisionError("unauthorized", "Sign in again to answer this.");
    if (body?.settled === true) {
      throw new ActionApprovalDecisionError(code, "This isn't waiting on you anymore, so nothing changed.", true);
    }
    if (code === "approval_not_executable") {
      // Recorded, but the action can't run, so the server ended the job. The
      // row keeps this message until the feed drops it.
      throw new ActionApprovalDecisionError(code, "You approved this, but it can no longer run safely, so nothing was done. Ask again to start it fresh.", false, readOutcome(body).conversation);
    }
    if (code === "approval_dispatch_pending") {
      throw new ActionApprovalDecisionError(code, "Your answer is saved but hasn't started yet. Try again.", false, readOutcome(body).conversation);
    }
    throw new ActionApprovalDecisionError(code || "network_error", "That didn't go through, so nothing changed. Try again.");
  }
}
